import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';
import Swiper from 'swiper';

import { SelectModalListItem } from '@/components/molecules/modal/select-modal';
import { useCompleteMissionMutation } from '@/hooks/mission/mission.gql.impl';
import { UserMissionTypeVo } from '@/hooks/mission/types';
import { useBroadcastChannel } from '@/hooks/mission/useBroadcastChannel';
import useMissionTimer from '@/hooks/mission/useMissionTimer';
import useAuthModal from '@/hooks/useAuthModal';
import useCustomRouter from '@/hooks/useCustomRouter';
import useModal from '@/hooks/useModal';
import useReportModal, { ReportData } from '@/hooks/useReportModal';
import useScreenFocus from '@/hooks/useScreenFocus';
import { tokenState } from '@/recoil/atoms/common/auth';
import { userState } from '@/recoil/atoms/common/user';
import { User } from '@/types/Auth';
import { QuestionAccessibleType } from '@/types/bff/gql-schema';
import { Nullable, ROUTES } from '@/types/common';
import { AttachmentBlindType } from '@/types/community';
import { MODAL_BUTTON_TYPE, MODAL_TYPE } from '@/types/modal';
import { AnswerLikeType, AnswererType, QnaAttachment, QnaDetailVo, RelatedQnaVo } from '@/types/qna';
import { getPageName, mobileLogger } from '@/utils/logger';
import { NATIVE_POST_MESSAGE_TYPE, historyBack, isWebView, sendPostMessageToNative } from '@/utils/mobile/RNPlugins';

import { EPixelEventName } from '@/constants/pixel';
import { ECategoryType } from '@/services/doctor/DiagnosisCategoryService';
import { useToast } from 'doctornow-design-system';

import Pixel from '@/utils/pixel/pixel';
import useAppDownloadCujModal from '../cuj/useAppDownloadCujModal';

import { DeeplinkLoggerEventName } from '@/constants/logs/deeplink';
import useAdBanner from '@/hooks/advertisement/useAdBanner';
import useNavigate from '@/hooks/useNavigate';
import { AdPageType, BannerAdExchangeModel } from '@/models/ad/list';
import { useAirbridgeDynamicLink } from '@/shared/hooks';
import { EAirbridgeLinkType } from '@/shared/types';
import { Client } from '@/types/app';
import useCanProgressQnaWrite from './mobile/write/useCanProgressQnaWrite';
import useGetQnaDetailWithNearSimplyQuestions from './useGetQnaDetailWithNearSimplyQuestions';
import useMakeQuestionPrivate from './useMakeQuestionPrivate';
import useGetRelatedQna from './useRelatedQna';
import useSetAnswerDeleteLike from './useSetAnswerDeleteLike';
import useSetAnswerLike from './useSetAnswerLike';
import useSetQuestionDisLike from './useSetQuestionDisLike';
import useSetQuestionLike from './useSetQuestionLike';

interface QuestionDetailViewModel {
  qnaDetail?: QnaDetailVo;
  isQnaDetailLoading: boolean;
  relatedQna?: RelatedQnaVo[];
  isRelatedQnaLoading: boolean;
  qnaAttachments: QnaDetailVo['attachments'];
  imageZoomIndex: number;
  pageName: string;
  onShareModal: boolean;
  user: User;
  showReportModal: boolean;
  reportItems: SelectModalListItem[];
  imageSlideIdx: number;
  isNotMine: boolean;
  isPrivateQuestion: boolean;
  timerVisible: boolean;
  displayTime: string;
  missionTargetTime: number;
  missionCompleted: boolean;
  questionBannerInfo: QuestionBannerInfoValueType;
  isShowPrivateModal: boolean;
  isMyQuestion: boolean;
  getStickyButtonText: () => string;
  shouldShowEventText: () => boolean;
  onShowModal: (e: React.MouseEvent, data: QuestionDetailModalDataType) => void;
  onVisibleDimmedImage: (index: number) => void;
  onInValidateDetailQuery: () => void;
  onClickImage: (index: number) => void;
  onCloseImageZoom: () => void;
  onClickLikeQuestion: (like: boolean) => void;
  onChangeShowShareModal: () => void;
  onClickLikeAnswer: (answerPid: string, type: AnswerLikeType, myLikeType: Nullable<AnswerLikeType>) => void;
  onClickMagazine: (magazinePid: string) => void;
  moveToDoctorDetailPage: (answererType: AnswererType, doctorId?: string | null) => void;
  onClickRelatedQna: (questionPid: string) => void;
  onCloseReportModal: () => void;
  onChangeImageBlindType: (index: number) => void;
  onChangeSlideImage: (swiper: Swiper) => void;
  onClickNearQna: (direction: string, questionPid?: string) => void;
  moveToHospitalDetail: (hospitalPid: string) => void;
  goBack: () => void;
  onClickWriteReview: (answerPid: string) => void;
  onClickStickButton: () => void;
  onClickBanner: () => void;
  toggleIsShowPrivateModal: () => void;
  onClickQuestionPrivate: () => void;
  targetQuestionCategory: QuestionCategoryType;
  onClickTopCta: () => void;
  onClickOuterLink: (url: string) => void;
  extractTextFromChildren: (children: React.ReactNode) => string;
  bannerAdExchange: BannerAdExchangeModel | null;
  handleSendExposureLog: () => void;
  subCategoryName?: string;
}

const questionCategoryMap: Record<string, QuestionCategoryType> = {
  '0': {
    categoryCode: 'NONE',
    categoryName: '없음',
    isRecommendedTreatment: false,
    diagnosisCategory: null,
  }, // 기본값
  '1': {
    categoryCode: 'FAMILY_MEDICINE',
    categoryName: '가정의학과',
    isRecommendedTreatment: true,
    diagnosisCategory: {
      categoryType: ECategoryType.Department,
      categoryTypeId: 1,
    },
  }, // 가정의학과
  '2': {
    categoryCode: 'INTERNAL_MEDICINE',
    categoryName: '내과',
    isRecommendedTreatment: true,
    diagnosisCategory: {
      categoryType: ECategoryType.Department,
      categoryTypeId: 3,
    },
  }, // 내과
  '3': {
    categoryCode: 'ANESTHESIOLOGY_AND_PAIN_MEDICINE',
    categoryName: '마취통증의학과',
    isRecommendedTreatment: true,
    diagnosisCategory: {
      categoryType: ECategoryType.Department,
      categoryTypeId: 15,
    },
  }, // 마취통증의학과
  '4': {
    categoryCode: 'UROLOGY',
    categoryName: '비뇨의학과',
    isRecommendedTreatment: true,
    diagnosisCategory: {
      categoryType: ECategoryType.Department,
      categoryTypeId: 9,
    },
  }, // 비뇨의학과
  '5': {
    categoryCode: 'OBSTETRICS_AND_GYNECOLOGY',
    categoryName: '산부인과',
    isRecommendedTreatment: true,
    diagnosisCategory: {
      categoryType: ECategoryType.Department,
      categoryTypeId: 10,
    },
  }, // 산부인과
  '6': {
    categoryCode: 'PLASTIC_SURGERY',
    categoryName: '성형외과',
    isRecommendedTreatment: false,
    diagnosisCategory: null,
  }, // 성형외과
  '7': {
    categoryCode: 'PEDIATRICS',
    categoryName: '소아청소년과',
    isRecommendedTreatment: true,
    diagnosisCategory: {
      categoryType: ECategoryType.Department,
      categoryTypeId: 2,
    },
  }, // 소아청소년과
  '8': {
    categoryCode: 'NEUROLOGY',
    categoryName: '신경과',
    isRecommendedTreatment: true,
    diagnosisCategory: {
      categoryType: ECategoryType.Department,
      categoryTypeId: 8,
    },
  }, // 신경과
  '9': {
    categoryCode: 'NEUROSURGERY',
    categoryName: '신경외과',
    isRecommendedTreatment: false,
    diagnosisCategory: null,
  }, // 신경외과
  '10': {
    categoryCode: 'OPHTHALMOLOGY',
    categoryName: '안과',
    isRecommendedTreatment: true,
    diagnosisCategory: {
      categoryType: ECategoryType.Department,
      categoryTypeId: 17,
    },
  }, // 안과
  '11': {
    categoryCode: 'RADIOLOGY',
    categoryName: '영상의학과',
    isRecommendedTreatment: false,
    diagnosisCategory: null,
  }, // 영상의학과
  '12': {
    categoryCode: 'SURGERY',
    categoryName: '외과',
    isRecommendedTreatment: false,
    diagnosisCategory: null,
  }, // 외과
  '13': {
    categoryCode: 'EMERGENCY_MEDICINE',
    categoryName: '응급의학과',
    isRecommendedTreatment: false,
    diagnosisCategory: null,
  }, // 응급의학과
  '14': {
    categoryCode: 'OTORHINOLARYNGOLOGY',
    categoryName: '이비인후과',
    isRecommendedTreatment: true,
    diagnosisCategory: {
      categoryType: ECategoryType.Department,
      categoryTypeId: 18,
    },
  }, // 이비인후과
  '15': {
    categoryCode: 'REHABILITATION_MEDICINE',
    categoryName: '재활의학과',
    isRecommendedTreatment: false,
    diagnosisCategory: null,
  }, // 재활의학과
  '16': {
    categoryCode: 'PSYCHIATRY',
    categoryName: '정신건강의학과',
    isRecommendedTreatment: true,
    diagnosisCategory: {
      categoryType: ECategoryType.Department,
      categoryTypeId: 7,
    },
  }, // 정신건강의학과
  '17': {
    categoryCode: 'ORTHOPEDICS',
    categoryName: '정형외과',
    isRecommendedTreatment: false,
    diagnosisCategory: null,
  }, // 정형외과
  '18': {
    categoryCode: 'DENTISTRY',
    categoryName: '치의학',
    isRecommendedTreatment: false,
    diagnosisCategory: null,
  }, // 치의학
  '19': {
    categoryCode: 'DERMATOLOGY',
    categoryName: '피부과',
    isRecommendedTreatment: true,
    diagnosisCategory: {
      categoryType: ECategoryType.Department,
      categoryTypeId: 6,
    },
  }, // 피부과
  '20': {
    categoryCode: 'ORIENTAL_MEDICINE',
    categoryName: '한의학',
    isRecommendedTreatment: true,
    diagnosisCategory: {
      categoryType: ECategoryType.Department,
      categoryTypeId: 19,
    },
  }, // 한의학
  '21': {
    categoryCode: 'COLD_FLU',
    categoryName: '감기/몸살',
    isRecommendedTreatment: true,
    diagnosisCategory: {
      categoryType: ECategoryType.Treatment,
      categoryTypeId: 1,
    },
  }, // 감기/몸살
  '22': {
    categoryCode: 'HYPERTENSION_DIABETES_HYPERLIPIDEMIA',
    categoryName: '고혈압/당뇨/고지혈증',
    isRecommendedTreatment: false,
    diagnosisCategory: null,
  }, // 고혈압/당뇨/고지혈증
  '23': {
    categoryCode: 'MENS_HEALTH',
    categoryName: '남성질환',
    isRecommendedTreatment: true,
    diagnosisCategory: {
      categoryType: ECategoryType.Treatment,
      categoryTypeId: 606,
    },
  }, // 남성질환
  '24': {
    categoryCode: 'DIET',
    categoryName: '다이어트',
    isRecommendedTreatment: true,
    diagnosisCategory: {
      categoryType: ECategoryType.Treatment,
      categoryTypeId: 12,
    },
  }, // 다이어트
  '25': {
    categoryCode: 'CHRONIC_DISEASE',
    categoryName: '만성질환',
    isRecommendedTreatment: true,
    diagnosisCategory: {
      categoryType: ECategoryType.Treatment,
      categoryTypeId: 5,
    },
  }, // 만성질환
  '26': {
    categoryCode: 'WOMENS_HEALTH',
    categoryName: '여성질환',
    isRecommendedTreatment: true,
    diagnosisCategory: {
      categoryType: ECategoryType.Treatment,
      categoryTypeId: 586,
    },
  }, // 여성질환
  '27': {
    categoryCode: 'DIGESTIVE_DISEASE',
    categoryName: '소화기질환',
    isRecommendedTreatment: true,
    diagnosisCategory: {
      categoryType: ECategoryType.Treatment,
      categoryTypeId: 577,
    },
  }, // 소화기질환
  '28': {
    categoryCode: 'POST_CONTRACEPTION',
    categoryName: '사후피임약',
    isRecommendedTreatment: false,
    diagnosisCategory: null,
  }, // 사후피임약
  '29': {
    categoryCode: 'ACNE_SKIN_DISEASE',
    categoryName: '여드름/피부염',
    isRecommendedTreatment: true,
    diagnosisCategory: {
      categoryType: ECategoryType.Treatment,
      categoryTypeId: 11,
    },
  }, // 여드름/피부염
  '30': {
    categoryCode: 'MENTAL_HEALTH',
    categoryName: '정신건강',
    isRecommendedTreatment: true,
    diagnosisCategory: {
      categoryType: ECategoryType.Treatment,
      categoryTypeId: 589,
    },
  }, // 정신건강
  '31': {
    categoryCode: 'CORONA_TREATMENT',
    categoryName: '코로나 치료',
    isRecommendedTreatment: true,
    diagnosisCategory: {
      categoryType: ECategoryType.Treatment,
      categoryTypeId: 14,
    },
  }, // 코로나 치료
  '32': {
    categoryCode: 'HAIR_LOSS',
    categoryName: '탈모',
    isRecommendedTreatment: true,
    diagnosisCategory: {
      categoryType: ECategoryType.Treatment,
      categoryTypeId: 9,
    },
  }, // 탈모
  '33': {
    categoryCode: 'PAIN',
    categoryName: '통증',
    isRecommendedTreatment: true,
    diagnosisCategory: {
      categoryType: ECategoryType.Treatment,
      categoryTypeId: 6,
    },
  }, // 통증
  '35': {
    categoryCode: 'PRESCRIPTION_DRUGS',
    categoryName: '처방약',
    isRecommendedTreatment: false,
    diagnosisCategory: null,
  }, // 처방약
  '36': {
    categoryCode: 'NUTRITIONAL_SUPPLEMENTS',
    categoryName: '영양제',
    isRecommendedTreatment: false,
    diagnosisCategory: null,
  }, // 영양제
  '37': {
    categoryCode: 'DIETARY_MEDICINE',
    categoryName: '다이어트 한약',
    isRecommendedTreatment: false,
    diagnosisCategory: null,
  }, // 다이어트 한약
  '38': {
    categoryCode: 'DIET',
    categoryName: '식단',
    isRecommendedTreatment: false,
    diagnosisCategory: null,
  }, // 식단
  '39': {
    categoryCode: 'OTHER_SYMPTOMS',
    categoryName: '기타증상',
    isRecommendedTreatment: false,
    diagnosisCategory: null,
  }, // 기타증상
} as const;

const questionBannerInfoId = {
  Default: 'default',
  Skin: 'skin',
  LowCost: 'lowcost',
  Awkward: 'awkward',
  Fast: 'fast',
  Baby: 'baby',
} as const;

type QuestionBannerInfoIdType = (typeof questionBannerInfoId)[keyof typeof questionBannerInfoId];

export type QuestionCategoryType = {
  categoryCode: string;
  categoryName: string;
  isRecommendedTreatment: boolean;
  diagnosisCategory?: DiagnosisCategory | null;
};

export type DiagnosisCategory = {
  categoryType: ECategoryType;
  categoryTypeId: number;
};

export type QuestionBannerInfoValueType = {
  headText: string;
  mainTextRows: string[];
  buttonText: string;
};

const questionBannerInfo: Record<QuestionBannerInfoIdType, QuestionBannerInfoValueType> = {
  default: {
    headText: '진료가 필요한 상황인가요?',
    mainTextRows: ['닥터나우는', '24시간 열려있어요!'],
    buttonText: '비대면 진료 받으러 가기',
  },
  skin: {
    headText: '피부 고민, 빠르게 해결하고 싶다면?',
    mainTextRows: ['진료받고 딱 맞는 약을', '최저가로 처방 받으세요!'],
    buttonText: '최저가로 비대면 진료받기',
  },
  lowcost: {
    headText: '더 싸고 가까운 병원, 약국 없나?',
    mainTextRows: ['최저가로 진료부터', '약 처방까지 가능해요!'],
    buttonText: '최저가로 비대면 진료받기',
  },
  awkward: {
    headText: '병원 가기 민망하다면?',
    mainTextRows: ['병원 방문 없이', '전화 진료 받아 보세요!'],
    buttonText: '마음 편히 진료받기',
  },
  fast: {
    headText: '빠르게 낫고 싶다면?',
    mainTextRows: ['집에서 5분 만에', '진료받을 수 있어요!'],
    buttonText: '지금 비대면 진료 받기',
  },
  baby: {
    headText: '지금 아이가 아프다면?',
    mainTextRows: ['닥터나우는 늦은 밤,', '주말에도 진료 가능해요!'],
    buttonText: '대기 없이 진료받기',
  },
};

const categoryToBannerMap: Partial<Record<keyof typeof questionCategoryMap, QuestionBannerInfoIdType>> = {
  '2': questionBannerInfoId.Fast, // 내과 → Fast
  '4': questionBannerInfoId.Awkward, // 비뇨기과 → Awkward
  '5': questionBannerInfoId.Awkward, // 산부인과 → Awkward
  '7': questionBannerInfoId.Baby, // 소아청소년과 → Baby
  '14': questionBannerInfoId.Fast, // 이비인후과 → Fast
  '19': questionBannerInfoId.Skin, // 피부과 → Skin
  '21': questionBannerInfoId.Fast, // 감기/몸살 → Fast
  '23': questionBannerInfoId.Awkward, // 남성질환 → Awkward
  '24': questionBannerInfoId.LowCost, // 다이어트 → LowCost
  '26': questionBannerInfoId.Awkward, // 여성질환 → Awkward
  '27': questionBannerInfoId.Fast, // 소화기질환 → Fast
  '29': questionBannerInfoId.Skin, // 여드름/피부염 → Skin
  '31': questionBannerInfoId.Fast, // 코로나 치료 → Fast
  '32': questionBannerInfoId.LowCost, // 탈모 → LowCost,
  '33': questionBannerInfoId.Fast, // 통증 → Fast
};

const getBannerInfoByCategory = (id: string) => {
  const categoryId = id as keyof typeof questionCategoryMap;
  const bannerId = categoryToBannerMap[categoryId] ?? questionBannerInfoId.Default;
  return questionBannerInfo[bannerId];
};

const getCategoryInfo = (id: string) => {
  const categoryId = id as keyof typeof questionCategoryMap;
  return questionCategoryMap[categoryId];
};

export type QuestionDetailModalDataType = { report?: ReportData; private?: any };

const useQuestionDetailViewModel = (client: Client): QuestionDetailViewModel => {
  const isFocused = useScreenFocus();
  const queryClient = useQueryClient();
  const { setShowAuthModal } = useAuthModal();
  const token = useRecoilValue(tokenState);
  const user = useRecoilValue(userState);
  const { router, moveToPage } = useCustomRouter();
  const questionPid = router.query?.id as string;
  const missionId = router.query?.missionId as string | undefined;
  const pageName = getPageName(router);
  const [imageZoomIndex, setImageZoomIndex] = useState<number>(-1);
  const [onShareModal, setOnShareModal] = useState<boolean>(false);
  const [imageSlideIdx, setImageSlideIdx] = useState(0);
  const [slideImages, setSlideImages] = useState<Array<QnaAttachment>>();
  const [isShowPrivateModal, setIsShowPrivateModal] = useState<boolean>(false);
  const broadCastChannel = useBroadcastChannel<UserMissionTypeVo>('user-missions');
  const navigate = useNavigate();
  const airbridgeDynamicLink = useAirbridgeDynamicLink();

  const toggleIsShowPrivateModal = () => {
    setIsShowPrivateModal((currentValue) => !currentValue);
  };

  const { bannerAdExchange, handleSendExposureLog } = useAdBanner(AdPageType.QnaDetailAnswerBottomBanner, client);
  const { showModal, closeModal } = useModal();
  const { guideToAppDownloadTemporary } = useCanProgressQnaWrite();
  const completeMissionMutation = useCompleteMissionMutation();
  const MISSION_TARGET_TIME = 10;
  const { displayTime, missionCompleted } = useMissionTimer({
    activated: Boolean(missionId),
    targetTime: MISSION_TARGET_TIME,
  });
  const { showReportModal, onShowReportModal, onCloseReportModal, reportItems } = useReportModal();

  // QNA 상세
  const {
    qnaDetail,
    isLoading: isQnaDetailLoading,
    refetchQnaDetail,
  } = useGetQnaDetailWithNearSimplyQuestions(questionPid);

  const [qnaAttachments, setQnaAttachments] = useState<QnaDetailVo['attachments']>(qnaDetail?.attachments ?? []);

  // 관련 질문 in QNA 상세
  const { relatedQna, isLoading: isRelatedQnaLoading } = useGetRelatedQna(questionPid);

  // 질문 좋아요 설정
  const { setLikeQuestionMutate } = useSetQuestionLike({
    onSuccess: () => onInValidateDetailQuery(),
  });

  // 질문 좋아요 해제
  const { setDisLikeQuestionMutate } = useSetQuestionDisLike({
    onSuccess: () => onInValidateDetailQuery(),
  });

  // 답변 좋아요 설정 : LIKE / DISLIKE 를 통해 도움이됬어요, 아쉬워요 구분
  const { setAnswerLikeMutate } = useSetAnswerLike({
    onSuccess: () => onInValidateDetailQuery(),
  });

  // 답변 좋아요 해제
  const { setAnswerDeleteLikeMutate } = useSetAnswerDeleteLike({
    onSuccess: () => onInValidateDetailQuery(),
  });

  // 질문 정보 저장
  const { setIsCujModalTrackingInfo } = useAppDownloadCujModal();

  // 질문 비공개
  const { makeQuestionPrivate } = useMakeQuestionPrivate();

  const { toast } = useToast();

  const subCategoryName = `QNA@${qnaDetail?.category?.type}@${qnaDetail?.category.questionCategoryId}`;

  const onClickQuestionPrivate = async () => {
    try {
      await makeQuestionPrivate(questionPid);
      refetchQnaDetail();
      toast({
        text: '비공개로 전환됐어요.',
        direction: 'bottom',
      });
      toggleIsShowPrivateModal();
    } catch (_error) {
      toast({
        text: '비공개로 전환에 실패했어요.',
        direction: 'bottom',
      });
    }
  };

  const isTreatmentCategory = qnaDetail?.category.type === ECategoryType.Treatment;
  const isDepartmentCategory = qnaDetail?.category.type === ECategoryType.Department;

  useEffect(() => {
    if (qnaDetail) {
      Pixel.trackCustom(EPixelEventName.PageIn, {
        ...(isTreatmentCategory && { symptom: qnaDetail.category.name }),
        ...(isDepartmentCategory && { subject: qnaDetail.category.name }),
      });

      setIsCujModalTrackingInfo({
        campaignName: 'web_content_qna',
        trackContent: 'web_mid_mdl_details',
        contentId: qnaDetail.questionPid,
        contentTitle: qnaDetail.title,
        contentCategory: qnaDetail.category.name,
        contentSubCategory: subCategoryName,
      });
    }
  }, [qnaDetail]);

  const isMyQuestion = qnaDetail?.user.userId === String(user?.userId);

  const questionBannerInfo = getBannerInfoByCategory(qnaDetail?.category.questionCategoryId ?? '1');

  const targetQuestionCategory = getCategoryInfo(qnaDetail?.category.questionCategoryId ?? '0');

  const getStickyButtonText = () => {
    if (isWebView()) {
      return '실시간 의료상담 물어보기';
    }

    if (targetQuestionCategory.categoryCode === 'POST_CONTRACEPTION') {
      return '빠르게 사후피임약 처방받기';
    }

    if (targetQuestionCategory.isRecommendedTreatment) {
      return '비대면 진료 받으러 가기';
    }

    return '지금 바로 의료 상담받기';
  };

  const shouldShowEventText = () => !isWebView();

  const onClickStickButton = () => {
    Pixel.trackCustom(EPixelEventName.BottomCtaClick, {
      ...(isTreatmentCategory && { symptom: qnaDetail.category.name }),
      ...(isDepartmentCategory && { subject: qnaDetail.category.name }),
    });

    guideToAppDownloadTemporary(targetQuestionCategory.isRecommendedTreatment && !isWebView(), {
      buttonText: getStickyButtonText(),
      qnaId: questionPid,
      qnaTitle: qnaDetail?.title,
      qnaCategory: qnaDetail?.category.name,
      qnaSubCategory: subCategoryName,
      linkType: shouldShowEventText() ? EAirbridgeLinkType.STORE_PAGE : EAirbridgeLinkType.GENERAL,
    });
  };

  const onClickBanner = () => {
    airbridgeDynamicLink.navigateWithTracking(DeeplinkLoggerEventName.ClickCtaButton, {
      campaign: 'web_content_qna',
      content: 'web_content_bnr_details',
      term: questionBannerInfo.buttonText,
      sub_id: questionPid,
      sub_id_1: qnaDetail?.title,
      sub_id_2: qnaDetail?.category.name,
      sub_id_3: subCategoryName,
    });
  };

  const onClickTopCta = () => {
    Pixel.trackCustom(EPixelEventName.TopCtaClick, {
      ...(isTreatmentCategory && { symptom: qnaDetail.category.name }),
      ...(isDepartmentCategory && { subject: qnaDetail.category.name }),
    });
  };

  // 질문 신고하기
  const onShowModal = (e: React.MouseEvent, data: QuestionDetailModalDataType) => {
    e.stopPropagation();

    if (!token) {
      // 로그인 정보가 없는 경우
      setShowAuthModal(true);
      return;
    }

    if (data.report) {
      onShowReportModal(data.report);
    }

    if (data.private) {
      toggleIsShowPrivateModal();
    }
  };

  // 이미지 dimmed 해제
  const onVisibleDimmedImage = useCallback(
    (index: number) => {
      const attachments = qnaAttachments.slice();
      if (attachments) {
        attachments[index as number].blindType = AttachmentBlindType.None;
        setQnaAttachments(attachments);
      }
    },
    [qnaAttachments]
  );

  const onInValidateDetailQuery = () => {
    queryClient.invalidateQueries();
  };

  // 질문에 첨부된 이미지 클릭 (dimmed 제거 / 확대)
  const onClickImage = (index: number) => {
    if (!qnaDetail?.isMine) {
      if (qnaAttachments[index as number].blindType === AttachmentBlindType.Blinded) {
        return;
      }
      if (qnaAttachments[index as number].blindType === AttachmentBlindType.Dimmed) {
        return onVisibleDimmedImage(index);
      }
    }
    setImageZoomIndex(index);
  };

  // 이미지 확대 모달 닫기
  const onCloseImageZoom = () => {
    setImageZoomIndex(-1);
  };

  // 관심질문 설정
  const onClickLikeQuestion = (like: boolean) => {
    if (!qnaDetail) return;
    if (!token) {
      setShowAuthModal(true);
    } else {
      if (like) {
        mobileLogger({
          logger: {
            eventName: 'quick_answer_question_like',
            pageName,
            payload: {
              questionId: qnaDetail.questionPid,
            },
          },
        });
        setLikeQuestionMutate({
          questionPid: qnaDetail.questionPid,
        });
      } else {
        mobileLogger({
          logger: {
            eventName: 'quick_answer_question_dislike',
            pageName,
            payload: {
              questionId: qnaDetail.questionPid,
            },
          },
        });
        setDisLikeQuestionMutate({
          questionPid: qnaDetail.questionPid,
        });
      }
    }
  };

  // 공유하기 모달 on/off
  const onChangeShowShareModal = () => {
    setOnShareModal(!onShareModal);
  };

  // 답변 도움이 됬어요 설정
  const onClickLikeAnswer = (answerPid: string, type: AnswerLikeType, myLikeType: Nullable<AnswerLikeType>) => {
    if (!token) {
      setShowAuthModal(true);
      return;
    }

    // 현재 상태가 null이 아니고 Like | DisLike 둘 중 하나일경우
    // 이미 선택해둔 값과 다시 클릭한 값이 같을 경우 취소
    if (myLikeType && type === myLikeType) {
      setAnswerDeleteLikeMutate({
        answerPid,
      });
      return;
    }

    setAnswerLikeMutate({
      request: {
        answerPid,
        type,
      },
    });
  };

  // 의료진이 추천한 콘텐츠
  const onClickMagazine = async (magazinePid: string) => {
    mobileLogger({
      logger: {
        eventName: 'recommended_magazine_click',
        pageName,
        payload: {
          id: magazinePid,
        },
      },
    });

    moveToPage({
      webLink: `${ROUTES.MAGAZINE.DETAIL.path}/${magazinePid}`,
      mobileScreenName: ROUTES.MAGAZINE.DETAIL.appScreenName,
      params: { id: magazinePid },
    });
  };

  // 의사 상세페이지 / 리스트로 이동
  const moveToDoctorDetailPage = (answererType: AnswererType, doctorId?: string | null) => {
    if (isWebView()) {
      mobileLogger({
        logger: {
          eventName: 'move_to_doctor_detail_page_click',
          pageName,
          payload: {
            doctorId: doctorId ?? '',
          },
        },
      });
      if (answererType === AnswererType.Doctor && doctorId) {
        sendPostMessageToNative(NATIVE_POST_MESSAGE_TYPE.MOVE_TO_DOCTOR_DETAIL_PAGE_AFTER_DIAGNOSIS_TARGET_CHECK, {
          doctorId,
        });
      }
    }
  };

  // 관련 질문
  const onClickRelatedQna = (questionPid: string) => {
    mobileLogger({
      logger: {
        eventName: 'related_qna_item_click',
        pageName,
        payload: {
          section_name: pageName,
          questionId: questionPid,
        },
      },
    });
    moveToPage({
      webLink: `${ROUTES.QNA.DETAIL.path}/${questionPid}`,
      mobileScreenName: ROUTES.QNA.DETAIL.appScreenName,
      params: { questionId: questionPid, missionId: null },
    });
  };

  const onChangeImageBlindType = (index: number) => {
    if (slideImages) {
      if (slideImages[index as number].blindType === AttachmentBlindType.Dimmed) {
        const images = slideImages.slice();
        if (images) {
          images[index as number].blindType = AttachmentBlindType.None;
          setSlideImages(images);
        }
      }
    }
  };

  const onChangeSlideImage = (swiper: Swiper) => {
    setImageSlideIdx(swiper.realIndex);
  };

  // 이전/다음 질문
  const onClickNearQna = (direction: string, questionPid?: string) => {
    if (!questionPid) return null;
    mobileLogger({
      logger: {
        eventName: 'click_near_qna',
        pageName,
        payload: {
          section_name: pageName,
          direction,
          questionId: questionPid,
        },
      },
    });

    moveToPage({
      webLink: `${ROUTES.QNA.DETAIL.path}/${questionPid}`,
      mobileScreenName: ROUTES.QNA.DETAIL.appScreenName,
      params: { questionId: questionPid },
    });
  };

  const moveToHospitalDetail = (affiliationPid: string) => {
    moveToPage({
      webLink: `${ROUTES.HOSPITAL.DETAIL.path}${affiliationPid}`,
      mobileScreenName: ROUTES.HOSPITAL.DETAIL.appScreenName,
      params: { hospitalPid: affiliationPid },
    });
  };

  const isPrivateQuestion = useMemo(
    () => Boolean(qnaDetail && qnaDetail.accessibleType !== QuestionAccessibleType.All),
    [qnaDetail]
  );

  const isNotMine = useMemo(() => Boolean(qnaDetail && !qnaDetail.isMine), [qnaDetail]);

  const goBack = () => {
    if (missionId && !missionCompleted) {
      showModal({
        type: MODAL_TYPE.ALERT,
        props: {
          title: '아직 미션이 완료되지 않았어요',
          message: '티켓을 받으려면 미션을 완료해야해요.',
          buttons: [
            {
              type: MODAL_BUTTON_TYPE.DEFAULT,
              label: '다음에 하기',
              onClick: historyBack,
            },
            {
              type: MODAL_BUTTON_TYPE.PRIMARY,
              label: '계속하기',
              onClick: closeModal,
            },
          ],
        },
      });
      return;
    }
    historyBack();
  };

  const onClickWriteReview = (answerPid: string) => {
    moveToPage({
      webLink: `${ROUTES.QNA.WRITE_ANSWER_REVIEW.path(questionPid, answerPid)}`,
      mobileScreenName: ROUTES.QNA.WRITE_ANSWER_REVIEW.appScreenName,
      params: {
        questionPid: questionPid,
        answerPid: answerPid,
      },
    });
  };

  const onClickOuterLink = (url: string) => {
    if (isWebView()) {
      navigate.navigateToPureWebViewAppScreen({
        uri: url,
      });
    }
  };

  const extractTextFromChildren = (children: React.ReactNode): string => {
    if (typeof children === 'string') {
      return children;
    }
    if (typeof children === 'number') {
      return String(children);
    }
    if (Array.isArray(children)) {
      return children.map(extractTextFromChildren).join('');
    }
    if (React.isValidElement(children) && children.props && children.props.children) {
      return extractTextFromChildren(children.props.children);
    }
    return '';
  };

  useEffect(() => {
    setQnaAttachments(qnaDetail?.attachments ?? []);
  }, [qnaDetail]);

  useEffect(() => {
    const images = qnaDetail?.isMine
      ? qnaAttachments
      : qnaAttachments.filter((attachment) => attachment.blindType !== AttachmentBlindType.Blinded);
    setSlideImages(images);
  }, [qnaDetail, qnaAttachments]);

  useEffect(() => {
    if (isNotMine && isPrivateQuestion) {
      if (isWebView()) {
        sendPostMessageToNative(NATIVE_POST_MESSAGE_TYPE.STACK_POP_TO_POP);
      } else {
        historyBack();
      }
      alert('비공개 질문입니다.');
    }
  }, [isPrivateQuestion, isNotMine]);

  useEffect(() => {
    if (!missionId || !missionCompleted) {
      return;
    }

    completeMissionMutation.mutate(
      {
        request: { missionId: missionId },
      },
      {
        onSuccess: () => {
          broadCastChannel.postMessage(UserMissionTypeVo.QnaRead);
        },
      }
    );
  }, [missionCompleted, missionId]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!isFocused) {
      return;
    }
    refetchQnaDetail();
  }, [isFocused]);

  return {
    bannerAdExchange,
    handleSendExposureLog,
    onClickTopCta,
    qnaDetail,
    isQnaDetailLoading,
    relatedQna,
    isRelatedQnaLoading,
    qnaAttachments,
    imageZoomIndex,
    pageName,
    onShareModal,
    user,
    showReportModal,
    reportItems: reportItems ?? [],
    imageSlideIdx,
    isNotMine: isNotMine,
    isPrivateQuestion: isPrivateQuestion,
    timerVisible: Boolean(missionId),
    displayTime: displayTime,
    missionCompleted: missionCompleted,
    missionTargetTime: MISSION_TARGET_TIME,
    questionBannerInfo,
    isShowPrivateModal,
    isMyQuestion,
    onShowModal,
    onVisibleDimmedImage,
    onInValidateDetailQuery,
    onClickImage,
    onCloseImageZoom,
    onClickLikeQuestion,
    onChangeShowShareModal,
    onClickLikeAnswer,
    onClickMagazine,
    moveToDoctorDetailPage,
    onClickRelatedQna,
    onCloseReportModal,
    onChangeImageBlindType,
    onChangeSlideImage,
    onClickNearQna,
    moveToHospitalDetail,
    goBack,
    onClickWriteReview,
    onClickOuterLink,
    getStickyButtonText,
    shouldShowEventText,
    onClickStickButton,
    onClickBanner,
    toggleIsShowPrivateModal,
    onClickQuestionPrivate,
    targetQuestionCategory,
    subCategoryName,
    extractTextFromChildren,
  };
};

export default useQuestionDetailViewModel;
